import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Image, Modal, Platform, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
	BottomButtons,
	BtnCloseModal,
	LightBtn,
	ProfileChipPng,
	SmallVipSix,
} from '../../../assets';
import i18n from '../../../i18n/i18n';
import { cleverTapService } from '../../../services/clevertap/clevertap.service';
import { deltaDNAService } from '../../../services/deltaDNA/deltadna.service';
import { analyticsEvents } from '../../../services/firebase/analytics-events.service';
import { RootState } from '../../../store';
import { updateUserVipStatus } from '../../../store/auth/authActions';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { USER_CANCELLED_PURCHASE } from '../../../store/iap/iapActions';
import {
	packagesRequest,
	paymentRequest,
	paymentReset,
} from '../../../store/packages/packagesActions';
import { Package, SUPPORTED_MODAL_ORIENTATIONS } from '../../../store/types';
import { env } from '../../../utils/env';
import { usePaymentStatus } from '../../../utils/hooks/paymentStatus';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useIAP } from '../../../utils/hooks/useIAP';
import { openWebPaymentWindow } from '../../../utils/webPaymentWindow';
import { RootStackParamList } from '../../navigator';
import { useFooterTop } from '../../Pages/footerHeight';
import AnimatedLoader from '../common/AnimatedLoader';
import { IndicatorScrollView } from '../common/ScrollIndicator';
import { TouchableOpacity } from '../common/Touchable';
import { ButtonCard } from '../Profile/ButtonCard';
import ChipScreen from '../Profile/BuyChips/ProfileChipScreen';
import VipScreen from '../Profile/BuyVip/ProfileVipScreen';
import ProfileTournaments from '../Profile/Tournaments/ProfileTournaments';
import StoreRedesignModal from '../Store/StoreRedesignModal';
import PaymentCheckoutModal, { PaymentProvider } from './PaymentCheckoutModal';

type BuyPackagesRouteProp = { params?: RootStackParamList['BuyPackages'] };
type ShopTab = 'chips' | 'vip' | 'tournaments';

/** The design's gap between the end of the VIP page (its price panel) and the nav. */
const VIP_PAGE_NAV_GAP = 8;
type Nav = NativeStackNavigationProp<RootStackParamList>;

interface BuyPackagesModalProps {
	initialActiveTab?: ShopTab;
	initialPlayerId?: string;
	onClose: () => void;
	route?: BuyPackagesRouteProp;
	renderInline?: boolean;
	renderVipPage?: boolean;
}

interface NavigationTabs {
	tab: Extract<ShopTab, 'chips' | 'vip'>;
	labelKey: 'T_SITE_BUY_VIP' | 'T_SITE_PROFILE_BUY';
	icon?: any;
}

function BuyPackagesModal({
	initialActiveTab = 'chips',
	initialPlayerId,
	onClose,
	route,
	renderInline = false,
	renderVipPage = false,
}: BuyPackagesModalProps) {
	const hasTrackedRef = useRef(false);
	const nativePurchaseLockRef = useRef(false);
	const nativePurchaseAttemptRef = useRef(0);
	const currentLanguage = i18n.language || 'bg';
	const { t } = useTranslation();
	const navigation = useNavigation<Nav>();
	const { navigate } = navigation;
	const { insets, isDesktop, isWeb, isNative, isMobile } = useDevice();
	const footerTop = useFooterTop();
	// Where the VIP page's scroll area ends on screen, to finish its content just above the nav.
	const vipPageRef = useRef<View>(null);
	const [vipPageBottom, setVipPageBottom] = useState<number | null>(null);
	const isLogged = useSelector(selectIsLogged);
	const dispatch = useDispatch();
	const packageState = useSelector((state: RootState) => state.package);
	const paymentData = usePaymentStatus();
	const iap = useIAP();
	const [selectedProvider, setSelectedProvider] = useState<PaymentProvider>('stripe');
	const [showProviderModal, setShowProviderModal] = useState(false);
	const [pendingPackage, setPendingPackage] = useState<Package | null>(null);
	const [, setPurchasedPackage] = useState<Package | null>(null);

	const [nativePaymentPackage, setNativePaymentPackage] = useState<Package | null>(null);

	// Keep the purchase loader visible for at least MIN_PURCHASE_LOADER_MS so a fast
	// deposit-creation round trip never makes it flash imperceptibly.
	const MIN_PURCHASE_LOADER_MS = 600;
	const purchaseLoaderStartRef = useRef(0);
	const [purchaseLoaderVisible, setPurchaseLoaderVisible] = useState(false);

	useEffect(() => {
		if (packageState.paymentLoading || iap.verifyLoading) {
			purchaseLoaderStartRef.current = Date.now();
			setPurchaseLoaderVisible(true);
		}
	}, [packageState.paymentLoading, iap.verifyLoading]);

	useEffect(() => {
		if (!purchaseLoaderVisible) return;
		if (packageState.paymentLoading || iap.verifyLoading) return;

		const remaining = MIN_PURCHASE_LOADER_MS - (Date.now() - purchaseLoaderStartRef.current);
		if (remaining <= 0) {
			setPurchaseLoaderVisible(false);
			return;
		}
		const timer = setTimeout(() => setPurchaseLoaderVisible(false), remaining);
		return () => clearTimeout(timer);
	}, [purchaseLoaderVisible, packageState.paymentLoading, iap.verifyLoading]);

	const nativeModalSafeAreaInsets = isNative
		? {
				paddingTop: Math.max(12, insets.top),
				paddingBottom: Math.max(12, insets.bottom),
				paddingLeft: Math.max(12, insets.left),
				paddingRight: Math.max(12, insets.right),
			}
		: null;
	const { items, paymentError } = packageState;
	const isEasyPay = isWeb && selectedProvider === 'easypay' && showProviderModal;
	const showPurchaseLoader = purchaseLoaderVisible;

	const getUserData = () => {
		if (isWeb) {
			return JSON.parse(localStorage.getItem('auth_user') || '{}');
		}
		return {};
	};

	const user = getUserData();

	const handlePaymentComplete = useCallback(
		(pkg: Package, status: 'success' | 'cancelled' | 'error') => {
			if (status !== 'success') return;

			if (pkg.type === 'VIP_MEMBERSHIP') {
				dispatch(updateUserVipStatus({ isVip: true }));
			}

			cleverTapService.trackEvent(`${pkg.type} Purchased`, {
				package_id: pkg.id,
				price: pkg.price,
				currency: (pkg as any)?.currency,
			});
		},
		[dispatch],
	);

	useEffect(() => {
		dispatch(paymentReset());
	}, [dispatch]);

	useEffect(() => {
		if (hasTrackedRef.current) return;

		hasTrackedRef.current = true;
		analyticsEvents.trackOpenShop();
		cleverTapService.trackEvent('Opened shop', { testShop: 'test' });
	}, []);

	useEffect(() => {
		if (hasTrackedRef.current) {
			console.log('⏭️ Already tracked, skipping...');
			return;
		}

		const trackShopOpen = async () => {
			hasTrackedRef.current = true;
			console.log('opening shop');
			analyticsEvents.trackOpenShop();
			cleverTapService.trackEvent('Opened shop', {
				testShop: 'test',
			});

			await deltaDNAService.openShopEvent(
				Platform.OS,
				currentLanguage,
				user.level,
				0,
				user.chips,
				'regular',
				'chips',
				1,
			);
		};

		trackShopOpen();
	}, [currentLanguage, user]);

	useEffect(() => {
		if (paymentError) {
			Alert.alert(
				t('T_ERROR', { defaultValue: 'Error' }),
				t(paymentError, { defaultValue: paymentError }),
				[{ text: t('T_OK', { defaultValue: 'OK' }) }],
			);
		}
	}, [paymentError, t]);

	useEffect(() => {
		dispatch(packagesRequest({ start: 0, limit: 20 }));
	}, [dispatch]);

	useEffect(() => {
		if (isWeb || items.length === 0) {
			return;
		}

		const iosPackages = items
			.filter(item => item.type === 'CHIPS' || item.type === 'VIP_MEMBERSHIP')
			.map(item => ({
				packageId: item.id,
				packageName: item.name,
				packageType: item.type,
				price: item.price,
				appleStoreProductId: item.metadata?.appleStore?.productId ?? null,
			}));

		console.log('[BuyPackages] Loaded iOS package metadata summary:', iosPackages);

		const duplicateAppleStoreProductIds = iosPackages.reduce(
			(acc, item) => {
				if (!item.appleStoreProductId) {
					return acc;
				}

				acc[item.appleStoreProductId] = acc[item.appleStoreProductId] || [];
				acc[item.appleStoreProductId].push({
					packageId: item.packageId,
					packageName: item.packageName,
					packageType: item.packageType,
					price: item.price,
				});
				return acc;
			},
			{} as Record<
				string,
				Array<{
					packageId: string;
					packageName: string;
					packageType: string;
					price: string | number | null | undefined;
				}>
			>,
		);

		const duplicates = Object.entries(duplicateAppleStoreProductIds).filter(
			([, mappedPackages]) => mappedPackages.length > 1,
		);

		if (duplicates.length > 0) {
			console.error(
				'[BuyPackages] Duplicate App Store product IDs detected across packages:',
				duplicates.map(([appleStoreProductId, mappedPackages]) => ({
					appleStoreProductId,
					mappedPackages,
				})),
			);
		}
	}, [isWeb, items]);

	useEffect(() => {
		if (isWeb) return;

		if (!iap.purchaseVerified || !nativePaymentPackage) {
			return;
		}

		handlePaymentComplete(nativePaymentPackage, 'success');

		if (nativePaymentPackage.type === 'VIP_MEMBERSHIP') {
			Alert.alert(
				t('T_SUCCESS', { defaultValue: 'Success' }),
				t('T_VIP_PURCHASE_SUCCESS', { defaultValue: 'VIP membership activated successfully!' }),
				[
					{
						text: t('T_OK', { defaultValue: 'OK' }),
						onPress: () => onClose(),
					},
				],
			);
		} else if (nativePaymentPackage.type === 'CHIPS') {
			Alert.alert(
				t('T_SUCCESS', { defaultValue: 'Success' }),
				t('T_CHIPS_PURCHASE_SUCCESS', { defaultValue: 'Chips purchased successfully!' }),
				[
					{
						text: t('T_OK', { defaultValue: 'OK' }),
						onPress: () => onClose(),
					},
				],
			);
		}

		nativePurchaseLockRef.current = false;
		iap.resetIAP();
		setNativePaymentPackage(null);
	}, [handlePaymentComplete, iap, isWeb, nativePaymentPackage, onClose, t]);

	useEffect(() => {
		if (isWeb || !iap.verifyError) {
			return;
		}

		Alert.alert(
			t('T_ERROR', { defaultValue: 'Error' }),
			t(iap.verifyError, { defaultValue: iap.verifyError }),
			[{ text: t('T_OK', { defaultValue: 'OK' }) }],
		);
		nativePurchaseLockRef.current = false;
		iap.resetIAP();
		setNativePaymentPackage(null);
	}, [iap, iap.verifyError, isWeb, t]);

	useEffect(() => {
		if (isWeb || !iap.purchaseError) {
			return;
		}

		// Backing out of the store's purchase sheet just returns to the shop.
		if (iap.purchaseError !== USER_CANCELLED_PURCHASE) {
			Alert.alert(
				t('T_ERROR', { defaultValue: 'Error' }),
				t(iap.purchaseError, { defaultValue: iap.purchaseError }),
				[{ text: t('T_OK', { defaultValue: 'OK' }) }],
			);
		}
		nativePurchaseLockRef.current = false;
		iap.resetIAP();
		setNativePaymentPackage(null);
	}, [iap, iap.purchaseError, isWeb, t]);

	useEffect(() => {
		console.log('[BuyPackages] Payment data:', paymentData);

		if (isWeb || paymentData.status !== 'success' || !paymentData.packageInfo) {
			return;
		}

		console.log('Payment successful for package:', paymentData.packageInfo);

		const pkg = paymentData.packageInfo;

		let vipDurationDays: number | null = null;
		let offerType: number;

		if (pkg.type === 'VIP_MEMBERSHIP') {
			if (pkg.price === 6.49) {
				vipDurationDays = 30;
			} else if (pkg.price === 19.99) {
				vipDurationDays = 180;
			} else {
				vipDurationDays = 365;
			}
			offerType = 1;
		} else {
			offerType = 2;
		}

		cleverTapService.trackEvent(`${pkg.type} Purchased`, {
			package_id: pkg.id,
			price: pkg.price,
			currency: pkg.currency,
		});

		deltaDNAService.playerPaymentEvent(
			Platform.OS,
			user.chips,
			currentLanguage,
			Array.isArray(pkg.currency) ? pkg.currency[0] : pkg.currency,
			pkg.price || 0,
			selectedProvider,
			pkg.id || '',
			pkg.id || '',
			pkg.name || '',
			vipDurationDays || pkg.chipAmount || 0,
			offerType,
		);

		if (pkg.type === 'VIP_MEMBERSHIP') {
			dispatch(updateUserVipStatus({ isVip: true }));

			Alert.alert(
				t('T_SUCCESS', { defaultValue: 'Success' }),
				t('T_VIP_PURCHASE_SUCCESS', { defaultValue: 'VIP membership activated successfully!' }),
				[
					{
						text: t('T_OK', { defaultValue: 'OK' }),
						onPress: () => onClose(),
					},
				],
			);
		} else if (pkg.type === 'CHIPS') {
			Alert.alert(
				t('T_SUCCESS', { defaultValue: 'Success' }),
				t('T_CHIPS_PURCHASE_SUCCESS', { defaultValue: 'Chips purchased successfully!' }),
				[
					{
						text: t('T_OK', { defaultValue: 'OK' }),
					},
				],
			);
		}
	}, [currentLanguage, selectedProvider, user, paymentData, dispatch, isWeb, t, onClose]);

	useEffect(() => {
		// An EasyPay code is shown in the checkout sheet; the shop stays open under it.
		if (!isWeb || !packageState.paymentSuccess || isEasyPay) {
			return;
		}

		// The checkout runs in a separate popup tab on web - close the shop modal
		// here and let the app-level PaymentStatusModal confirm the result. Wait out
		// the minimum loader time so the loader is always visibly shown first.
		const remaining = MIN_PURCHASE_LOADER_MS - (Date.now() - purchaseLoaderStartRef.current);
		if (remaining > 0) {
			const timer = setTimeout(onClose, remaining);
			return () => clearTimeout(timer);
		}
		onClose();
	}, [isWeb, packageState.paymentSuccess, isEasyPay, onClose]);

	useEffect(() => {
		if (isWeb) return;
		if (!paymentData.status) return;

		if (paymentData.status === 'success' && paymentData.packageInfo) {
			handlePaymentComplete(paymentData.packageInfo as Package, 'success');
		} else if (paymentData.status === 'cancelled') {
			Alert.alert(
				t('T_CANCELLED', { defaultValue: 'Cancelled' }),
				t('T_PAYMENT_CANCELLED', { defaultValue: 'Payment was cancelled.' }),
				[{ text: t('T_OK', { defaultValue: 'OK' }) }],
			);
		} else {
			Alert.alert(
				t('T_ERROR', { defaultValue: 'Error' }),
				t('T_PAYMENT_FAILED', { defaultValue: 'Payment failed. Please try again.' }),
				[{ text: t('T_OK', { defaultValue: 'OK' }) }],
			);
		}
	}, [paymentData, t, handlePaymentComplete, isWeb]);

	// -------------------------
	// Helpers
	// -------------------------
	const getFrontendUrl = (): string => {
		const configuredFrontendUrl = env('SVARA_WEB_URL')!;

		if (Platform.OS !== 'web') {
			return configuredFrontendUrl;
		}

		try {
			const w = window as any;
			const origin = w?.location && typeof w.location.origin === 'string' ? w.location.origin : '';
			return origin || configuredFrontendUrl;
		} catch {
			return configuredFrontendUrl;
		}
	};

	const frontendUrl = getFrontendUrl();

	// -------------------------
	// Payment logic (unchanged)
	// -------------------------
	const handlePayment = (pkg: Package, provider: PaymentProvider = selectedProvider) => {
		setPurchasedPackage(pkg);
		setNativePaymentPackage(pkg);
		analyticsEvents.trackBeginCheckout(pkg.id);

		// EasyPay has no checkout page - the player gets a code to pay at an office - so it needs no tab.
		if (isWeb && provider !== 'easypay') {
			// Open the checkout tab synchronously within the user gesture so it is not
			// blocked as a popup. The saga navigates it once the checkout URL is ready.
			openWebPaymentWindow();
		}

		dispatch(
			paymentRequest({
				amount: Number(pkg.price || 0),
				pkg,
				currency: (pkg as any)?.currency?.name || (pkg as any)?.currency || 'EUR',
				frontendUrl,
				provider,
			}),
		);
	};

	const handleBuyPress = (pkg: Package) => {
		if (!isWeb && nativePurchaseLockRef.current) {
			console.log(
				'[BuyPackages] Ignoring buy press because a native purchase is already in flight',
				{
					packageId: pkg.id,
					packageName: pkg.name,
				},
			);
			return;
		}

		if (!isLogged) {
			onClose();
			navigate('Login');
			return;
		}

		if (isWeb) {
			setPendingPackage(pkg);
			setShowProviderModal(true);
			return;
		}

		if (iap.isSupported && iap.isInitialized) {
			nativePurchaseAttemptRef.current += 1;
			console.log('[BuyPackages] Starting native IAP purchase attempt', {
				attempt: nativePurchaseAttemptRef.current,
				packageId: pkg.id,
				packageName: pkg.name,
				packageType: pkg.type,
				price: pkg.price,
				appleStoreProductId: pkg.metadata?.appleStore?.productId,
			});
			nativePurchaseLockRef.current = true;
			setPurchasedPackage(pkg);
			setNativePaymentPackage(pkg);
			analyticsEvents.trackBeginCheckout(pkg.id);
			iap.purchasePackage(pkg);
		} else {
			console.log('[BuyPackages] IAP not available, using web payment:', pkg);
			handlePayment(pkg);
		}
	};

	const handleProviderSelect = (provider: PaymentProvider) => {
		setSelectedProvider(provider);
		// EasyPay answers with a payment code, which the checkout sheet stays open to show.
		if (provider === 'easypay') {
			if (pendingPackage) handlePayment(pendingPackage, provider);
			return;
		}
		setShowProviderModal(false);
		if (pendingPackage) handlePayment(pendingPackage, provider);
		setPendingPackage(null);
	};

	const handleProviderClose = () => {
		setShowProviderModal(false);
		setPendingPackage(null);
		if (isEasyPay) dispatch(paymentReset());
	};

	const [activeTab, setActiveTab] = useState<ShopTab>(route?.params?.activeTab || initialActiveTab);
	const [playerId, setPlayerId] = useState<string | undefined>(
		route?.params?.playerId || initialPlayerId,
	);
	useEffect(() => {
		setActiveTab(route?.params?.activeTab || initialActiveTab);
		setPlayerId(route?.params?.playerId || initialPlayerId);
	}, [initialActiveTab, initialPlayerId, route?.params?.activeTab, route?.params?.playerId]);

	const showBottomTabs = activeTab !== 'tournaments';
	const useStoreRedesign = activeTab !== 'tournaments' && !renderVipPage;

	const chipPackages = useMemo(
		() =>
			items.filter(item => item.type === 'CHIPS').sort((a, b) => (a?.price || 0) - (b?.price || 0)),
		[items],
	);

	const vipPackages = useMemo(
		() =>
			items
				.filter(item => item.type === 'VIP_MEMBERSHIP')
				.sort((a, b) => (a?.price || 0) - (b?.price || 0))
				.map(item => ({ ...item, name: item.name.replace(/VIP/gi, '') })),
		[items],
	);

	const topTabs: Array<NavigationTabs> = [
		{
			tab: 'chips',
			labelKey: 'T_SITE_PROFILE_BUY',
			icon: ProfileChipPng,
		},
		{
			tab: 'vip',
			labelKey: 'T_SITE_BUY_VIP',
			icon: SmallVipSix,
		},
	];

	const shopContent = (
		<>
			{activeTab === 'chips' && (
				<ChipScreen chipPackages={chipPackages} onPayment={handleBuyPress} />
			)}

			{activeTab === 'vip' && <VipScreen vipPackages={vipPackages} onPayment={handleBuyPress} />}

			{activeTab === 'tournaments' && <ProfileTournaments playerId={playerId} />}

			{paymentError && (
				<View style={styles.errorBox}>
					<Text style={styles.errorText}>{t(paymentError, { defaultValue: paymentError })}</Text>
				</View>
			)}
		</>
	);

	// Figma "VIP / Mobile — VIP Club Subscription" (2583:1064) ends the page with the price panel 8
	// above the nav. The footer floats over the bottom of this scroll area on native and sits a
	// little below it on web, so the area reaches down to the footer's top edge and its content is
	// padded by however far the footer reaches into it, plus those 8.
	const footerOverlap =
		vipPageBottom != null && footerTop != null
			? vipPageBottom - footerTop
			: // No footer measured - the desktop layout's dock, whose Home notch rises into the page -
				// keeps the clearance the page always had.
				isNative
				? 110
				: 64;
	const vipPageBottomPadding = Math.max(0, footerOverlap) + VIP_PAGE_NAV_GAP;
	const vipPageReach = Math.min(0, footerOverlap);

	const modalContent = renderVipPage ? (
		<View
			ref={vipPageRef}
			style={styles.vipPageContainer}
			onLayout={() =>
				vipPageRef.current?.measureInWindow((_x, y, _w, h) => setVipPageBottom(y + h))
			}
		>
			{/* No gutter for the bar: the page is centred with its own 15 margins (Figma V3, 6202:33673),
			    and the bar runs in the right margin. */}
			<IndicatorScrollView
				reserveGutter={false}
				style={[styles.vipPageScroll, { marginBottom: vipPageReach }]}
				contentContainerStyle={[styles.vipPageContent, { paddingBottom: vipPageBottomPadding }]}
				showsVerticalScrollIndicator={false}
				nestedScrollEnabled
			>
				{shopContent}
			</IndicatorScrollView>

			{showPurchaseLoader && (
				<View style={styles.loaderOverlay}>
					<AnimatedLoader scale={1.1} />
				</View>
			)}
		</View>
	) : useStoreRedesign ? (
		<StoreRedesignModal
			chipPackages={chipPackages}
			vipPackages={vipPackages}
			initialTab={activeTab}
			error={paymentError}
			loading={packageState.loading}
			onBuy={handleBuyPress}
			onClose={onClose}
			onGoToClub={() => {
				onClose();
				setTimeout(() => navigate('VipSubscription'), 0);
			}}
			renderInline={renderInline}
		/>
	) : (
		<View
			style={[styles.modalOverlay, nativeModalSafeAreaInsets, { minWidth: isDesktop ? 800 : 300 }]}
		>
			{!showPurchaseLoader && (
				<TouchableOpacity activeOpacity={1} onPress={onClose} style={styles.backdropTapArea} />
			)}
			<View
				style={[
					styles.modalContainer,
					{ width: isMobile ? '90%' : 'auto' },
					{ height: isMobile ? '80%' : 'auto' },
				]}
			>
				<IndicatorScrollView
					style={styles.scrollContainer}
					contentContainerStyle={[styles.scrollContent, { paddingBottom: isNative ? 65 : 0 }]}
					showsVerticalScrollIndicator={false}
					nestedScrollEnabled
				>
					<View style={styles.contentBody}>{shopContent}</View>
				</IndicatorScrollView>

				<View style={styles.closeButtonContainer}>
					<TouchableOpacity onPress={onClose} style={styles.closeBtn}>
						<Image source={BtnCloseModal} style={styles.closeIcon} />
					</TouchableOpacity>
				</View>

				{showBottomTabs && (
					<View
						style={[
							styles.bottomButtons,
							isNative && {
								transform: [{ translateY: -25 }],
							},
						]}
					>
						{topTabs.map(tabConfig => (
							<View
								key={tabConfig.tab}
								style={{
									...(isMobile ? { flex: 1, minWidth: 0 } : { width: 196 }),
								}}
							>
								<ButtonCard
									activeImage={LightBtn}
									inactiveImage={BottomButtons}
									label={t(tabConfig.labelKey)}
									onPress={() => setActiveTab(tabConfig.tab)}
									active={activeTab === tabConfig.tab}
									icon={tabConfig.icon}
									testId={`cta-buy-packages-tab-${tabConfig.tab}`}
								/>
							</View>
						))}
					</View>
				)}

				{showPurchaseLoader && (
					<View style={styles.loaderOverlay}>
						<AnimatedLoader scale={1.1} />
					</View>
				)}
			</View>
		</View>
	);

	return (
		<>
			{useStoreRedesign ? (
				modalContent
			) : renderInline ? (
				modalContent
			) : (
				<Modal
					transparent
					visible
					statusBarTranslucent
					navigationBarTranslucent
					supportedOrientations={SUPPORTED_MODAL_ORIENTATIONS}
				>
					{modalContent}
				</Modal>
			)}

			{/* Web only: native buys go through the store's in-app purchase. */}
			{isWeb && (
				<PaymentCheckoutModal
					pkg={pendingPackage}
					visible={showProviderModal}
					easyPay={
						isEasyPay && packageState.payment?.paymentCode
							? {
									paymentCode: packageState.payment.paymentCode,
									transactionId: packageState.payment.transactionId ?? '',
								}
							: null
					}
					easyPayLoading={isEasyPay && packageState.paymentLoading}
					easyPayError={isEasyPay ? paymentError : undefined}
					onSelect={handleProviderSelect}
					onClose={handleProviderClose}
				/>
			)}
		</>
	);
}

const styles = StyleSheet.create({
	modalOverlay: {
		flex: 1,
		backgroundColor: 'rgba(0,0,0,0.7)',
		justifyContent: 'center',
		alignItems: 'center',
	},
	bottomButtons: {
		flexDirection: 'row',
		justifyContent: 'center',
		width: '100%',
		marginTop: 6,
	},
	androidBottomButtons: {
		marginBottom: 40,
	},

	modalContainer: {
		maxHeight: '80%',
		minHeight: '40%',
		backgroundColor: '#112345',
		borderRadius: 10,
		borderWidth: 1,
		borderColor: '#00f0ff',
		position: 'relative',
		alignSelf: 'center',
		zIndex: 1,
	},
	backdropTapArea: {
		...StyleSheet.absoluteFill,
	},
	scrollContainer: {
		flex: 1,
		width: '100%',
		padding: 16,
	},
	scrollContent: {
		flexGrow: 1,
		justifyContent: 'center',
	},
	contentBody: {
		width: '100%',
		flexGrow: 1,
		justifyContent: 'center',
	},
	vipPageContainer: {
		flex: 1,
		width: '100%',
		minHeight: 0,
	},
	vipPageScroll: {
		flex: 1,
		width: '100%',
	},
	vipPageContent: {
		flexGrow: 1,
		alignItems: 'center',
		paddingTop: 4,
	},
	errorBox: {
		backgroundColor: '#331010',
		paddingVertical: 10,
		paddingHorizontal: 16,
		borderRadius: 20,
		alignSelf: 'center',
		marginTop: 15,
		marginBottom: 20,
	},
	errorText: {
		color: '#ff7b7b',
		fontSize: 13,
		fontWeight: '500',
		textAlign: 'center',
	},
	closeButtonContainer: {
		position: 'absolute',
		top: -24,
		right: -4,
		zIndex: 20,
	},
	closeBtn: {
		width: 50,
		height: 50,
	},
	closeIcon: {
		width: 50,
		height: 50,
	},
	loaderOverlay: {
		...StyleSheet.absoluteFill,
		backgroundColor: 'rgba(0,0,0,0.55)',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 30,
	},
});

export default BuyPackagesModal;
